/**
 * One home for "how an integration run reads" - the Status chip label/colour and
 * the Detail copy. Both the run-history table and the section shell's latest-result
 * chip call this, so a run can never imply success and failure in the same place.
 *
 * Status fix: a run whose job finished (`status === 'success'`) but whose connection
 * result failed (`summary.ok === false`) reads as a failed run, not a green success.
 */
import type {
  IntegrationRun,
  IntegrationRunProgress,
} from '../../../../../services/admin/integrationsApi'

type RunColor = 'success' | 'error' | 'info' | 'default'

export interface RunDisplay {
  /** Friendly status word for the chip (Success / Failed / Running / ...). */
  label: string
  /** MUI chip colour matching the (effective) status. */
  color: RunColor
  /** Outcome copy for the Detail cell. */
  detail: string
  /** True while the run is still in flight - drives the spinner + live timer. */
  isRunning: boolean
}

const STATUS_LABEL: Record<string, string> = {
  success: 'Success',
  error: 'Failed',
  running: 'Running',
}

const STATUS_COLOR: Record<string, RunColor> = {
  success: 'success',
  error: 'error',
  running: 'info',
}

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s

/** Live "what it's doing now" line from a running run's progress payload. */
const formatProgress = (
  p: IntegrationRunProgress | null | undefined,
): string => {
  if (!p || !p.note) return 'In progress…'
  if (typeof p.current === 'number' && typeof p.total === 'number' && p.total > 0) {
    return `${p.note} ${p.current} of ${p.total}`
  }
  return p.note
}

/**
 * Compact "what it did" line from a finished run's summary, built generically
 * from the count keys every integration sync follows (`*_synced` / `*_matched`
 * / `*_unmatched`, with all `*_deleted` folded into one total). Returns null for
 * a summary with no such counts (e.g. a health probe), so the caller can fall
 * back to its connection copy. Integration-agnostic - no per-integration keys.
 */
export const summarizeCounts = (
  summary: Record<string, unknown> | null,
): string | null => {
  if (!summary) return null
  // Bucket by kind so the line reads in a stable order (the headline `_synced`
  // counts, then matched / unmatched, then a single folded delete total),
  // regardless of the summary's key order.
  const synced: string[] = []
  let matched: string | null = null
  let unmatched: string | null = null
  let deleted = 0
  for (const [key, value] of Object.entries(summary)) {
    if (typeof value !== 'number') continue
    if (key.endsWith('_deleted')) {
      deleted += value
    } else if (value <= 0) {
      continue
    } else if (key.endsWith('_unmatched')) {
      unmatched = `${value} unmatched`
    } else if (key.endsWith('_matched')) {
      matched = `${value} matched`
    } else if (key.endsWith('_synced')) {
      synced.push(`${value} ${key.slice(0, -'_synced'.length)}`)
    }
  }
  const parts = [...synced]
  if (matched) parts.push(matched)
  if (unmatched) parts.push(unmatched)
  if (deleted > 0) parts.push(`${deleted} deleted`)
  return parts.length ? parts.join(' · ') : null
}

export const deriveRunDisplay = (run: IntegrationRun): RunDisplay => {
  const ok = (run.summary as { ok?: boolean } | null)?.ok

  let detail: string
  if (run.status === 'running') {
    detail = formatProgress(run.progress)
  } else if (run.error) {
    detail = run.error
  } else if (ok === true) {
    detail = 'Connection OK'
  } else if (ok === false) {
    detail = 'Connection failed'
  } else {
    detail = summarizeCounts(run.summary) ?? '-'
  }

  // A finished job whose connection result failed reads as a failed run.
  const effective =
    run.status === 'success' && ok === false ? 'error' : run.status

  return {
    label: STATUS_LABEL[effective] ?? capitalize(effective),
    color: STATUS_COLOR[effective] ?? 'default',
    detail,
    isRunning: run.status === 'running',
  }
}
